/**
 * batch-67 middelen-inname (FO-499 §A4) - de pure kern van het gedeelde
 * uitgifte-/innameformulier: types, de richting per regel, de validatie en de
 * concept-buffer. Los van React en van `@streep/types` (dit pakket kent die
 * afhankelijkheid niet): de app zet de waarden om naar het gedeelde
 * request-schema en valideert daar nog eens mee. De regels hier zijn dezelfde
 * als in dat schema, zodat de gebruiker de fout inline ziet vóór het versturen.
 */

export type MiddelFormulierModus = 'UITGIFTE' | 'INNAME' | 'OVERDRACHT' | 'WISSEL'
export type MiddelFormulierStaat = 'GOED' | 'GEBRUIKSSPOREN' | 'BESCHADIGD' | 'ONVOLLEDIG'
export type MiddelFormulierRichting = 'IN' | 'UIT' | 'OVER'
/** Afboeken: kapot (het middel is terug, foto verplicht) of kwijt (geen foto mogelijk). */
export type MiddelFormulierAfboekSoort = 'KAPOT' | 'VERMIST'

export const MIDDEL_FORMULIER_AFBOEK_SOORTEN: ReadonlyArray<{ value: MiddelFormulierAfboekSoort; label: string }> = [
  { value: 'KAPOT', label: 'Kapot' },
  { value: 'VERMIST', label: 'Kwijt' },
]

export const MIDDEL_FORMULIER_STATEN: ReadonlyArray<{ value: MiddelFormulierStaat; label: string }> = [
  { value: 'GOED', label: 'Goed' },
  { value: 'GEBRUIKSSPOREN', label: 'Gebruikssporen' },
  { value: 'BESCHADIGD', label: 'Beschadigd' },
  { value: 'ONVOLLEDIG', label: 'Onvolledig' },
]

/** Eén middel op het formulier (aangeleverd door de app). */
export interface MiddelFormulierItem {
  /** Unieke sleutel van de regel binnen het formulier. */
  key: string
  middelType: string
  middelId: string
  omschrijving: string
  kenmerk?: string | null
  detail?: string | null
  /** Startaantal (bulk); default 1. */
  aantal?: number
  /** Bulk-artikel: het aantal is aan te passen. */
  aantalBewerkbaar?: boolean
  maxAantal?: number
  /** WISSEL: de vaste richting van deze regel (IN = komt terug, UIT = gaat mee). */
  richting?: 'IN' | 'UIT'
  /**
   * Bij inname of bulk: een (voorraad)locatie is verplicht. Een functie maakt
   * dat afhankelijk van wat er op de regel is ingevuld (bv. alleen bij een staat
   * waarin het middel terug op voorraad gaat).
   */
  vereistLocatie?: boolean | ((regel: MiddelFormulierRegelWaarde) => boolean)
  /** Bij uitgifte: een verwachte retourdatum is verplicht (retourplichtige categorie). */
  vereistRetourdatum?: boolean
  /** Afboeken (kwijt/kapot) is voor dit middel mogelijk. Default true bij inname. */
  kanAfboeken?: boolean
  /**
   * Dit middel kent geen staat en geen foto (bv. een abonnement dat wordt
   * losgekoppeld): de regel staat op GOED en toont geen staat- of fotokeuze.
   */
  zonderStaat?: boolean
}

/** De ingevulde staat van één regel. */
export interface MiddelFormulierRegelWaarde {
  key: string
  middelType: string
  middelId: string
  aantal: number
  richting?: 'IN' | 'UIT'
  conditie: MiddelFormulierStaat | null
  opmerking: string
  afboeken: boolean
  afboekSoort: MiddelFormulierAfboekSoort | null
  afboekReden: string
  locatieId: string | null
  /** ISO-dag (jjjj-mm-dd) of null. */
  verwachteRetour: string | null
  fotoIds: string[]
}

export interface MiddelFormulierWaarden {
  plek: string
  opmerking: string
  /** PNG data-URL of null. */
  handtekeningAfgever: string | null
  handtekeningOntvanger: string | null
  regels: MiddelFormulierRegelWaarde[]
}

/** Richting van een regel: uit de modus, of bij WISSEL uit de regel zelf. */
export function middelFormulierRichting(
  modus: MiddelFormulierModus,
  regel: { richting?: 'IN' | 'UIT' },
): MiddelFormulierRichting {
  if (modus === 'UITGIFTE') return 'UIT'
  if (modus === 'INNAME') return 'IN'
  if (modus === 'OVERDRACHT') return 'OVER'
  return regel.richting ?? 'IN'
}

/** Welke handtekeningen de modus verplicht stelt (FO-499 §5.3). */
export function verplichteHandtekeningen(modus: MiddelFormulierModus): { afgever: boolean; ontvanger: boolean } {
  return {
    // Wie afgeeft tekent bij inname en overdracht.
    afgever: modus === 'INNAME' || modus === 'OVERDRACHT',
    // Wie ontvangt tekent bij uitgifte, overdracht en wissel.
    ontvanger: modus !== 'INNAME',
  }
}

/** Startwaarde van een regel. Bij uitgifte is de staat standaard GOED (nieuw of nagekeken). */
export function legeRegelWaarde(modus: MiddelFormulierModus, item: MiddelFormulierItem): MiddelFormulierRegelWaarde {
  const richting = middelFormulierRichting(modus, item)
  return {
    key: item.key,
    middelType: item.middelType,
    middelId: item.middelId,
    aantal: Math.max(1, item.aantal ?? 1),
    ...(item.richting ? { richting: item.richting } : {}),
    conditie: richting === 'UIT' || item.zonderStaat ? 'GOED' : null,
    opmerking: '',
    afboeken: false,
    afboekSoort: null,
    afboekReden: '',
    locatieId: null,
    verwachteRetour: null,
    fotoIds: [],
  }
}

/**
 * Is bij deze regel een bewijsfoto verplicht? Bij een staat anders dan goed, en
 * bij afboeken als kapot; een vermist middel valt niet te fotograferen.
 */
export function fotoVerplichtVoorRegel(regel: Pick<MiddelFormulierRegelWaarde, 'conditie' | 'afboeken' | 'afboekSoort'>): boolean {
  if (regel.afboeken) return regel.afboekSoort === 'KAPOT'
  return regel.conditie !== null && regel.conditie !== 'GOED'
}

/**
 * De regel onder de naam van een middel: het kenmerk en het detail, zonder herhaling.
 * De omschrijving noemt het kenmerk vaak al ("SN-123 · Boormachine"), en het detail
 * soms ook ("Nr. SN-123"): dan staat het er één keer.
 */
export function regelOndertitel(item: Pick<MiddelFormulierItem, 'omschrijving' | 'kenmerk' | 'detail'>): string {
  const kenmerk = item.kenmerk?.trim() || null
  const detail = item.detail?.trim() || null
  const noemt = (tekst: string | null, deel: string) => (tekst ?? '').toLowerCase().includes(deel.toLowerCase())
  const toonKenmerk = kenmerk !== null && !noemt(item.omschrijving, kenmerk) && !noemt(detail, kenmerk)
  return [toonKenmerk ? kenmerk : null, detail].filter((x): x is string => Boolean(x)).join(' · ')
}

/** Vraagt deze regel (met wat er nu is ingevuld) om een locatie? */
export function locatieVereistVoorRegel(item: MiddelFormulierItem | undefined, regel: MiddelFormulierRegelWaarde): boolean {
  const vereist = item?.vereistLocatie
  return typeof vereist === 'function' ? vereist(regel) : Boolean(vereist)
}

/** Veldpad van een regel-fout: `regels.<key>.<veld>` (stabiel bij verwijderen of herordenen). */
export function regelFoutPad(key: string, veld: string): string {
  return `regels.${key}.${veld}`
}

/**
 * Valideer het formulier. Geeft een map veldpad -> melding terug (leeg = goed).
 * Zelfde regels als het gedeelde request-schema:
 *  - minimaal één middel;
 *  - een staat bij alles wat terugkomt of wordt overgedragen (niet bij afboeken);
 *  - minimaal één foto bij een staat anders dan GOED en bij afboeken als kapot;
 *  - bij afboeken de keuze kapot of kwijt, en een reden;
 *  - een locatie waar het middel teruggelegd of afgeboekt wordt;
 *  - een verwachte retourdatum bij een retourplichtige uitgifte;
 *  - de handtekening(en) die de modus vraagt.
 */
export function valideerMiddelFormulier(
  modus: MiddelFormulierModus,
  waarden: MiddelFormulierWaarden,
  items: ReadonlyArray<MiddelFormulierItem>,
): Record<string, string> {
  const fouten: Record<string, string> = {}
  if (waarden.regels.length === 0) fouten['regels'] = 'Voeg minimaal een middel toe.'
  const itemByKey = new Map(items.map((i) => [i.key, i]))

  for (const regel of waarden.regels) {
    const item = itemByKey.get(regel.key)
    const richting = middelFormulierRichting(modus, regel)
    if (modus === 'WISSEL' && !regel.richting) {
      fouten[regelFoutPad(regel.key, 'richting')] = 'Geef aan of dit middel terugkomt of wordt meegegeven.'
    }
    if (!Number.isInteger(regel.aantal) || regel.aantal < 1) {
      fouten[regelFoutPad(regel.key, 'aantal')] = 'Het aantal is minimaal 1.'
    } else if (item?.maxAantal != null && regel.aantal > item.maxAantal) {
      fouten[regelFoutPad(regel.key, 'aantal')] = `Het aantal is hooguit ${item.maxAantal}.`
    }
    if (richting !== 'UIT' && !regel.afboeken && !regel.conditie) {
      fouten[regelFoutPad(regel.key, 'conditie')] = 'Geef de staat op waarin het middel is ingeleverd.'
    }
    if (fotoVerplichtVoorRegel(regel) && regel.fotoIds.length < 1) {
      fouten[regelFoutPad(regel.key, 'fotoIds')] = regel.afboeken
        ? 'Maak minimaal een foto van het kapotte middel.'
        : 'Maak minimaal een foto bij een staat anders dan goed.'
    }
    if (regel.afboeken && !regel.afboekSoort) {
      fouten[regelFoutPad(regel.key, 'afboekSoort')] = 'Kies of het middel kapot of kwijt is.'
    }
    if (regel.afboeken && richting !== 'IN') {
      fouten[regelFoutPad(regel.key, 'afboeken')] = 'Afboeken kan alleen bij een middel dat wordt ingenomen.'
    }
    if (regel.afboeken && regel.afboekReden.trim().length === 0) {
      fouten[regelFoutPad(regel.key, 'afboekReden')] = 'Reden is verplicht bij afboeken (kwijt of kapot).'
    }
    if (locatieVereistVoorRegel(item, regel) && !regel.afboeken && richting !== 'OVER' && !regel.locatieId) {
      fouten[regelFoutPad(regel.key, 'locatieId')] =
        richting === 'IN' ? 'Kies de locatie waar het middel teruggelegd wordt.' : 'Kies de voorraadlocatie voor dit artikel.'
    }
    if (item?.vereistRetourdatum && richting === 'UIT' && !regel.verwachteRetour) {
      fouten[regelFoutPad(regel.key, 'verwachteRetour')] = 'Vul de verwachte retourdatum in.'
    }
  }

  const verplicht = verplichteHandtekeningen(modus)
  if (verplicht.ontvanger && !waarden.handtekeningOntvanger) {
    fouten['handtekeningOntvanger'] = 'De ontvanger moet tekenen voor ontvangst.'
  }
  if (verplicht.afgever && !waarden.handtekeningAfgever) {
    fouten['handtekeningAfgever'] =
      modus === 'INNAME' ? 'De medewerker die inlevert moet tekenen voor de inname.' : 'De medewerker die het middel afgeeft moet tekenen.'
  }
  return fouten
}

// ─── Concept-buffer ──────────────────────────────────────────────────────────

/** Een concept ouder dan dit aantal dagen wordt niet meer teruggezet en opgeruimd. */
export const MIDDEL_FORMULIER_CONCEPT_DAGEN = 7

/** Een middel zoals het in het concept bewaard wordt: alleen platte gegevens. */
export type MiddelFormulierConceptItem = Omit<MiddelFormulierItem, 'vereistLocatie'> & { vereistLocatie?: boolean }

/**
 * Wat er van een half ingevuld formulier bewaard wordt: de tekstuele invoer per
 * regel, de sessievelden en welke middelen er op het formulier stonden (zodat
 * een regel die de gebruiker zelf toevoegde een herlaad overleeft). Foto's en
 * handtekeningen horen er bewust NIET bij (een handtekening hoort niet in
 * browseropslag te blijven staan, en een foto is te groot); die worden na een
 * herlaad opnieuw gevraagd. Een concept vervalt na `MIDDEL_FORMULIER_CONCEPT_DAGEN`.
 */
export interface MiddelFormulierConcept {
  /** ISO-moment waarop het concept is bewaard. */
  bewaardOp: string
  plek: string
  opmerking: string
  regels: Record<string, Pick<MiddelFormulierRegelWaarde, 'aantal' | 'richting' | 'conditie' | 'opmerking' | 'afboeken' | 'afboekSoort' | 'afboekReden' | 'locatieId' | 'verwachteRetour'>>
  items: MiddelFormulierConceptItem[]
}

function naarConceptItem(item: MiddelFormulierItem): MiddelFormulierConceptItem {
  return {
    key: item.key,
    middelType: item.middelType,
    middelId: item.middelId,
    omschrijving: item.omschrijving,
    ...(item.kenmerk != null ? { kenmerk: item.kenmerk } : {}),
    ...(item.detail != null ? { detail: item.detail } : {}),
    ...(item.aantal !== undefined ? { aantal: item.aantal } : {}),
    ...(item.aantalBewerkbaar !== undefined ? { aantalBewerkbaar: item.aantalBewerkbaar } : {}),
    ...(item.maxAantal !== undefined ? { maxAantal: item.maxAantal } : {}),
    ...(item.richting ? { richting: item.richting } : {}),
    // Een voorwaarde als functie is niet te bewaren; de app levert zo'n regel zelf opnieuw aan.
    ...(typeof item.vereistLocatie === 'boolean' ? { vereistLocatie: item.vereistLocatie } : {}),
    ...(item.vereistRetourdatum !== undefined ? { vereistRetourdatum: item.vereistRetourdatum } : {}),
    ...(item.kanAfboeken !== undefined ? { kanAfboeken: item.kanAfboeken } : {}),
    ...(item.zonderStaat !== undefined ? { zonderStaat: item.zonderStaat } : {}),
  }
}

/** Is dit bewaarde concept te oud (of zonder datum, dus van onbekende leeftijd)? */
export function conceptVerlopen(ruw: unknown, nu: Date = new Date()): boolean {
  if (!ruw || typeof ruw !== 'object') return true
  const bewaardOp = (ruw as { bewaardOp?: unknown }).bewaardOp
  if (typeof bewaardOp !== 'string') return true
  const t = Date.parse(bewaardOp)
  if (Number.isNaN(t)) return true
  return nu.getTime() - t > MIDDEL_FORMULIER_CONCEPT_DAGEN * 24 * 60 * 60 * 1000
}

/**
 * De middelen die in een bewaard concept op het formulier stonden. Defensief
 * gelezen: alleen regels met de verplichte velden in de juiste vorm komen terug.
 * De app beslist welke daarvan opnieuw op het formulier mogen (een middel dat de
 * medewerker intussen niet meer heeft, hoort er niet meer op).
 */
export function conceptItems(ruw: unknown, nu: Date = new Date()): MiddelFormulierConceptItem[] {
  if (conceptVerlopen(ruw, nu)) return []
  const lijst = (ruw as { items?: unknown }).items
  if (!Array.isArray(lijst)) return []
  const uit: MiddelFormulierConceptItem[] = []
  const gezien = new Set<string>()
  for (const x of lijst) {
    if (!x || typeof x !== 'object') continue
    const i = x as Record<string, unknown>
    if (typeof i.key !== 'string' || typeof i.middelType !== 'string' || typeof i.middelId !== 'string' || typeof i.omschrijving !== 'string') continue
    if (gezien.has(i.key)) continue
    gezien.add(i.key)
    const getal = (v: unknown) => (typeof v === 'number' && Number.isInteger(v) && v >= 1 ? v : undefined)
    const aantal = getal(i.aantal)
    const maxAantal = getal(i.maxAantal)
    uit.push({
      key: i.key,
      middelType: i.middelType,
      middelId: i.middelId,
      omschrijving: i.omschrijving,
      ...(typeof i.kenmerk === 'string' ? { kenmerk: i.kenmerk } : {}),
      ...(typeof i.detail === 'string' ? { detail: i.detail } : {}),
      ...(aantal !== undefined ? { aantal } : {}),
      ...(typeof i.aantalBewerkbaar === 'boolean' ? { aantalBewerkbaar: i.aantalBewerkbaar } : {}),
      ...(maxAantal !== undefined ? { maxAantal } : {}),
      ...(i.richting === 'IN' || i.richting === 'UIT' ? { richting: i.richting } : {}),
      ...(typeof i.vereistLocatie === 'boolean' ? { vereistLocatie: i.vereistLocatie } : {}),
      ...(typeof i.vereistRetourdatum === 'boolean' ? { vereistRetourdatum: i.vereistRetourdatum } : {}),
      ...(typeof i.kanAfboeken === 'boolean' ? { kanAfboeken: i.kanAfboeken } : {}),
      ...(typeof i.zonderStaat === 'boolean' ? { zonderStaat: i.zonderStaat } : {}),
    })
  }
  return uit
}

export function naarConcept(
  waarden: MiddelFormulierWaarden,
  items: ReadonlyArray<MiddelFormulierItem> = [],
  nu: Date = new Date(),
): MiddelFormulierConcept {
  const regels: MiddelFormulierConcept['regels'] = {}
  for (const r of waarden.regels) {
    regels[r.key] = {
      aantal: r.aantal,
      ...(r.richting ? { richting: r.richting } : {}),
      conditie: r.conditie,
      opmerking: r.opmerking,
      afboeken: r.afboeken,
      afboekSoort: r.afboekSoort,
      afboekReden: r.afboekReden,
      locatieId: r.locatieId,
      verwachteRetour: r.verwachteRetour,
    }
  }
  return { bewaardOp: nu.toISOString(), plek: waarden.plek, opmerking: waarden.opmerking, regels, items: items.map(naarConceptItem) }
}

const STATEN = new Set<string>(MIDDEL_FORMULIER_STATEN.map((s) => s.value))

/**
 * Lees een bewaard concept terug en leg het over de verse startwaarden. Defensief:
 * opslag kan leeg, verminkt, verlopen of van een oudere versie zijn; alles wat
 * niet klopt valt terug op de startwaarde. Een regel die niet meer op het
 * formulier staat wordt genegeerd.
 *
 * `opties.keys` beperkt het terugzetten tot die regels (regels die later op het
 * formulier komen krijgen dan alsnog hun bewaarde invoer, zonder wat de
 * gebruiker intussen elders invulde te overschrijven); `opties.sessievelden`
 * false laat plek en opmerking met rust.
 */
export function pasConceptToe(
  start: MiddelFormulierWaarden,
  ruw: unknown,
  opties: { keys?: ReadonlySet<string>; sessievelden?: boolean; nu?: Date } = {},
): MiddelFormulierWaarden {
  if (!ruw || typeof ruw !== 'object') return start
  if (conceptVerlopen(ruw, opties.nu)) return start
  const c = ruw as Partial<MiddelFormulierConcept>
  const regelsBron = c.regels && typeof c.regels === 'object' ? (c.regels as Record<string, unknown>) : {}
  const sessievelden = opties.sessievelden !== false
  return {
    ...start,
    plek: sessievelden && typeof c.plek === 'string' ? c.plek : start.plek,
    opmerking: sessievelden && typeof c.opmerking === 'string' ? c.opmerking : start.opmerking,
    regels: start.regels.map((regel) => {
      if (opties.keys && !opties.keys.has(regel.key)) return regel
      const b = regelsBron[regel.key]
      if (!b || typeof b !== 'object') return regel
      const r = b as Record<string, unknown>
      return {
        ...regel,
        aantal: typeof r.aantal === 'number' && Number.isInteger(r.aantal) && r.aantal >= 1 ? r.aantal : regel.aantal,
        ...(regel.richting === undefined && (r.richting === 'IN' || r.richting === 'UIT') ? { richting: r.richting } : {}),
        conditie: typeof r.conditie === 'string' && STATEN.has(r.conditie) ? (r.conditie as MiddelFormulierStaat) : regel.conditie,
        opmerking: typeof r.opmerking === 'string' ? r.opmerking : regel.opmerking,
        afboeken: typeof r.afboeken === 'boolean' ? r.afboeken : regel.afboeken,
        afboekSoort: r.afboekSoort === 'KAPOT' || r.afboekSoort === 'VERMIST' ? r.afboekSoort : regel.afboekSoort,
        afboekReden: typeof r.afboekReden === 'string' ? r.afboekReden : regel.afboekReden,
        locatieId: typeof r.locatieId === 'string' ? r.locatieId : regel.locatieId,
        verwachteRetour:
          typeof r.verwachteRetour === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(r.verwachteRetour) ? r.verwachteRetour : regel.verwachteRetour,
      }
    }),
  }
}

// ─── Foto-upload ─────────────────────────────────────────────────────────────

export const MIDDEL_FOTO_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
export const MIDDEL_FOTO_MAX_BYTES = 25 * 1024 * 1024
export const MIDDEL_FOTO_MAX_AANTAL = 10

/** Client-side voorcontrole van een gekozen foto; `null` = in orde. De API valideert opnieuw. */
export function controleerFotoBestand(file: { type: string; size: number }, maxBytes = MIDDEL_FOTO_MAX_BYTES): string | null {
  if (!(MIDDEL_FOTO_MIME_TYPES as readonly string[]).includes(file.type)) return 'Alleen JPEG, PNG of WebP toegestaan.'
  if (file.size === 0) return 'De foto is leeg.'
  if (file.size > maxBytes) return `De foto is groter dan ${Math.round(maxBytes / (1024 * 1024))} MB.`
  return null
}
